<template>
  <div class="relative inline-block">
    <button
      ref="trigger"
      class="dropdown-trigger border-[1px] border-[#E2E2E2] p-[10px] text-[#626262] rounded-[8px] flex items-center gap-[8px]"
      @click="toggle"
    >
      <slot name="placeholder">
        Filter
      </slot>
      <span class="k-arrow-down-1 pointer-events-none text-base" />
    </button>
    <div
      v-if="isOpen"
      class="dropdown-panel absolute bg-white border-[1px] border-[#E2E2E2] rounded-[8px] shadow-[0_0_12px_0_rgba(0,0,0,0.12)] z-[11] p-[8px] min-w-[160px]"
      :style="panelStyle"
    >
      <slot>
        <div
          v-for="(option, index) in options"
          :key="option.value"
          ref="optionRow"
          class="option-row relative mb-[8px] last:mb-0"
          :class="{ 'submenu-anchor': activeOptionIndex === index && hasChildren(option) }"
          @mouseenter="onOptionEnter(option, index)"
          @mouseleave="onOptionLeave"
        >
          <button
            type="button"
            class="w-full flex items-center justify-between gap-[8px] p-[10px] text-[#626262] rounded-[8px] hover:bg-[#F5F5F5] text-left"
            @click="onOptionClick(option, index)"
          >
            {{ option.text }}
            <span
              v-if="hasChildren(option)"
              class="k-arrow-right-2 pointer-events-none text-base"
            />
          </button>
          <div
            v-show="activeOptionIndex === index && hasChildren(option)"
            class="submenu-panel fixed bg-white rounded-[8px] shadow-[0_4px_8px_0_rgba(0,0,0,0.1)] z-[12] p-[8px] min-w-[140px] border border-[#E2E2E2]"
            :class="{ 'submenu--right': submenuOnRight, 'submenu--left': !submenuOnRight }"
            :style="submenuStyle"
            @mouseenter="onSubmenuEnter"
            @mouseleave="onSubmenuLeave"
          >
            <component
              :is="option.component"
              :active="activeOptionIndex === index"
              :selected-options="selectedOptions[option.value] || []"
              v-bind="getComponentProps(option)"
              @on-apply="onApply"
            />
          </div>
        </div>
      </slot>
    </div>
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[10]"
      aria-hidden="true"
      @click="close"
    />
  </div>
</template>

<script>
import FilterMarketplace from './FilterMarketplace.vue'
import FilterSeller from './FilterSeller.vue'

export default {
  name: 'DropdownNested',
  props: {
    options: {
      type: Array,
      default: () => ([
        {
          text: 'Marketplace',
          value: 'marketplace',
          component: FilterMarketplace,
        },
        {
          text: 'Seller',
          value: 'seller',
          component: FilterSeller,
        },
      ]),
    },
    selectedOptions: {
      type: Object,
      default: () => ({ marketplace: [], seller: [] }),
    },
  },
  data() {
    return {
      isOpen: false,
      panelStyle: {},
      anchorSupported: false,
      activeOptionIndex: null,
      submenuStyle: {},
      submenuOnRight: true,
      submenuMinWidth: 140,
      gap: 4,
      closeSubmenuTimer: null,
    }
  },
  mounted() {
    this.anchorSupported = typeof CSS !== 'undefined' && CSS.supports?.('top', 'anchor(bottom)')
  },
  beforeDestroy() {
    this.cancelCloseSubmenu()
  },
  methods: {
    /**
     * Map the component props to the component
     * @param option {Object} - The option object
     * @returns {Object} - The component props
     */
    getComponentProps(option) {
      const custom = option.componentProps
      if (typeof custom === 'function') return custom()
      return custom || {}
    },
    hasChildren(option) {
      return (option.children && option.children.length > 0) || option.component
    },
    cancelCloseSubmenu() {
      if (this.closeSubmenuTimer) {
        clearTimeout(this.closeSubmenuTimer)
        this.closeSubmenuTimer = null
      }
    },
    scheduleCloseSubmenu() {
      this.cancelCloseSubmenu()
      this.closeSubmenuTimer = setTimeout(() => {
        this.activeOptionIndex = null
        this.closeSubmenuTimer = null
      }, 200)
    },
    onApply(payload) {
      this.$emit('apply', payload)
      this.close()
    },
    onOptionEnter(option, index) {
      this.cancelCloseSubmenu()
      this.activeOptionIndex = index
      this.$nextTick(() => this.updateSubmenuPosition(index))
    },
    onOptionLeave() {
      this.scheduleCloseSubmenu()
    },
    onSubmenuEnter() {
      this.cancelCloseSubmenu()
    },
    onSubmenuLeave() {
      this.scheduleCloseSubmenu()
    },
    updateSubmenuPosition(index) {
      const row = this.$refs.optionRow ? this.$refs.optionRow[index] : null
      if (!row) return
      const rowRect = row.getBoundingClientRect()
      const spaceRight = window.innerWidth - rowRect.right
      const spaceLeft = rowRect.left
      const showOnRight = spaceRight >= this.submenuMinWidth || spaceRight >= spaceLeft
      this.submenuOnRight = showOnRight

      if (this.anchorSupported) {
        this.submenuStyle = { minWidth: `${this.submenuMinWidth}px` }
        return
      }
      if (showOnRight) {
        this.submenuStyle = {
          top: `${rowRect.top}px`,
          left: `${rowRect.right + this.gap}px`,
          minWidth: `${this.submenuMinWidth}px`,
          transform: 'none',
        }
      } else {
        this.submenuStyle = {
          top: `${rowRect.top}px`,
          left: `${rowRect.left - this.gap}px`,
          minWidth: `${this.submenuMinWidth}px`,
          transform: 'translateX(-100%)',
        }
      }
    },
    onOptionClick(option, index) {
      if (this.hasChildren(option)) {
        this.activeOptionIndex = this.activeOptionIndex === index ? null : index
        if (this.activeOptionIndex !== null) {
          this.$nextTick(() => this.updateSubmenuPosition(index))
        }
      } else {
        this.$emit('select', option)
        this.close()
      }
    },
    onChildClick(child) {
      this.$emit('select', child)
      this.close()
    },
    toggle() {
      this.isOpen = !this.isOpen
      this.cancelCloseSubmenu()
      if (this.isOpen) {
        this.$nextTick(this.updatePosition)
      } else {
        this.activeOptionIndex = null
      }
    },
    close() {
      this.isOpen = false
      this.activeOptionIndex = null
      this.cancelCloseSubmenu()
    },
    updatePosition() {
      if (this.anchorSupported || !this.$refs.trigger) return
      const rect = this.$refs.trigger.getBoundingClientRect()
      this.panelStyle = {
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        minWidth: `${rect.width}px`,
      }
    },
  },
}
</script>

<style scoped>
.dropdown-trigger {
  anchor-name: --dropdown-trigger;
}

/* Row yang aktif jadi anchor untuk submenu (children) */
.option-row.submenu-anchor {
  anchor-name: --submenu-anchor;
}

@supports (top: anchor(bottom)) {
  .dropdown-panel {
    position-anchor: --dropdown-trigger;
    left: anchor(left);
    min-width: anchor-size(width);
    margin-top: 4px;
  }

  .submenu-panel.submenu--right {
    position-anchor: --submenu-anchor;
    top: anchor(top);
    left: anchor(right);
    margin-left: 4px;
  }

  .submenu-panel.submenu--left {
    position-anchor: --submenu-anchor;
    top: anchor(top);
    left: anchor(right);
    margin-right: 4px;
  }
}
</style>
